// Форматирането на прозореца „История на операциите".
//
// Отделен модул, защото едни и същи правила за пари, количества, часове и
// имена важат за хедъра, кутийките с тоталите, реда от хронологията и
// списъка с артикули — четири файла, които иначе биха ги преписали.

import dayjs from "dayjs";

import { EM_DASH, parseDecimal } from "../../../../components/dataTable";
import { fmtEuro } from "../../../../utils/currency";

/** Минус U+2212 — по-широк от дефиса и подравнен с цифрите. */
export const MINUS = "−";

/**
 * Сума със знак: „+12.99 €" / „−12.99 €".
 * Знакът се лепи отпред, а не се оставя на `toFixed`, за да е U+2212.
 */
export const formatSignedMoney = (value: number): string =>
  `${value < 0 ? MINUS : "+"}${fmtEuro(Math.abs(value))}`;

/**
 * Сума, която по конструкция ВАДИ (сторно, отстъпка, прехвърлено — BE ги
 * връща отрицателни). Минусът стои и пред нулата, за да личи посоката на
 * клетката; положителна стойност значи надбавка и получава „+".
 */
export const formatDeductedMoney = (value: number): string =>
  value > 0 ? `+${fmtEuro(value)}` : `${MINUS}${fmtEuro(Math.abs(value))}`;

/** Цели количества без нули след запетаята, дробните — с три знака. */
export const formatQuantity = (quantity: string | number): string => {
  const parsed = parseDecimal(quantity);
  return parsed % 1 === 0 ? parsed.toFixed(0) : parsed.toFixed(3);
};

/**
 * Часът на операцията. Секундите участват в подредбата, но не се показват —
 * иначе две операции в една и съща минута изглеждат разбъркани.
 */
export const formatTime = (iso: string): string => dayjs(iso).format("HH:mm");

/**
 * Датата в хедъра. Годината излиза само когато сметката НЕ е от текущата —
 * при справка за днес четирите цифри са шум.
 */
export const formatShortDate = (iso: string): string => {
  const parsed = dayjs(iso);
  return parsed.year() === dayjs().year()
    ? parsed.format("DD.MM")
    : parsed.format("DD.MM.YYYY");
};

/** Името на артикула на текущия език; BG и EN са резервите по ред. */
export const itemName = (
  names: Record<string, string> | undefined,
  language: string,
): string => names?.[language] || names?.bg || names?.en || EM_DASH;

/**
 * Новата единична цена, изведена от знаковия процент на събитието — така
 * историята показва цената КЪМ МОМЕНТА на промяната, не текущата на реда.
 */
export const adjustedPrice = (
  price: string | number,
  percent: number,
): number => {
  const base = parseDecimal(price);
  return Math.round(((base * (100 - percent)) / 100) * 100) / 100;
};

/** Тоновете на чиповете в хронологията — единственият цвят в прозореца. */
export type EventTone =
  | "neutral"
  | "brand"
  | "ok"
  | "danger"
  | "info"
  | "violet";

/** Tinted фон без рамка — рецептата на чипа от дизайна. */
export const TONE_CHIP_CLASS: Record<EventTone, string> = {
  neutral: "bg-fill text-soft",
  brand: "bg-brand-tint text-brand-deep-ink",
  ok: "bg-ok-tint text-ok-deep",
  danger: "bg-danger-tint text-danger-ink",
  info: "bg-info-tint text-info-deep",
  violet: "bg-violet-tint text-violet",
};
